/**
 * GA4 + tracked-share-link instrumentation for the sim pages.
 *
 * GA4 fires for everyone (the gtag snippet lives in each page's <head>).
 * The D1-backed share tracking only activates when the URL carries
 * ?sl=<token> — a link generated from the admin "Sims" tab — and reports
 * fire-and-forget to /api/sim-share/event.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export type SimId = "air" | "convoy";

export type SimTracker = {
  /** GA4 event, automatically prefixed `{sim}_sim_` with page_path attached. */
  event: (name: string, params?: Record<string, unknown>) => void;
  /** D1 share-link event; no-op unless the page was opened via ?sl=<token>. */
  shareEvent: (event: "run" | "export", detail?: string) => void;
};

export function makeTracker(sim: SimId): SimTracker {
  const token = new URLSearchParams(window.location.search).get("sl");

  const ga = (name: string, params?: Record<string, unknown>): void => {
    window.gtag?.("event", name, {
      event_category: "demo",
      page_path: window.location.pathname,
      ...params,
    });
  };

  const post = (event: string, detail?: string): void => {
    if (!token) return;
    fetch("/api/sim-share/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, event, detail }),
      keepalive: true,
    }).catch(() => {});
  };

  if (token) {
    ga(`${sim}_sim_share_visit`, { event_category: "engagement", event_label: token });
    post("visit");
  }

  return {
    event: (name, params) => ga(`${sim}_sim_${name}`, params),
    shareEvent: post,
  };
}

/**
 * The subset of `state` that differs from `defaults`, as a URL query string —
 * the payload of the Share button and of admin scenario-locked links.
 */
export function encodeConfigQuery<T extends Record<string, unknown>>(state: T, defaults: T): string {
  const qs = new URLSearchParams();
  for (const k of Object.keys(defaults)) {
    const v = state[k];
    if (v !== defaults[k] && (typeof v === "number" || typeof v === "string")) {
      qs.set(k, String(v));
    }
  }
  return qs.toString();
}

/**
 * Read config overrides from the current URL. Only keys present in
 * `defaults` are accepted; values are coerced to the default's type and
 * silently dropped when malformed. Callers still validate enum fields.
 */
export function parseConfigQuery<T extends Record<string, unknown>>(defaults: T): Partial<T> {
  const out: Record<string, unknown> = {};
  const qs = new URLSearchParams(window.location.search);
  for (const [k, raw] of qs) {
    if (!(k in defaults)) continue;
    const d = defaults[k];
    if (typeof d === "number") {
      const n = Number(raw);
      if (Number.isFinite(n)) out[k] = n;
    } else if (typeof d === "string") {
      out[k] = raw;
    }
  }
  return out as Partial<T>;
}

/** Copy text to the clipboard, with a fallback for non-secure contexts. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}
